<template>
  <Teleport to="body">
    <Transition name="dialog">
      <div v-if="visible" class="dialog-mask" @click.self="handleCancel">
        <div class="dialog-content" :data-type="type">
          <div class="dialog-header">
            <h3 class="dialog-title">
              {{ localTitle }}
            </h3>
            <button v-if="showClose" class="dialog-close" @click="handleCancel">×</button>
          </div>
          <div class="dialog-body">
            <slot>{{ message }}</slot>
          </div>
          <div class="dialog-footer" v-if="showFooter">
            <CustomButton
              v-if="showCancelButton"
              :type="cancelButtonType"
              width="100px"
              @click="handleCancel"
              class="dialog-cancel-btn"
            >
              {{ cancelButtonText }}
            </CustomButton>
            <CustomButton
              v-if="showConfirmButton"
              :type="confirmButtonType"
              width="100px"
              @click="handleConfirm"
              class="dialog-confirm-btn"
            >
              {{ confirmButtonText }}
            </CustomButton>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { watch, computed } from 'vue'
import CustomButton from '@/components/button/CustomButton.vue'

interface Props {
  // 是否显示对话框
  visible: boolean
  // 对话框标题
  title: string
  // 对话框消息内容
  message: string
  // 是否显示关闭按钮
  showClose: boolean
  // 是否显示底部按钮区域
  showFooter: boolean
  // 是否显示取消按钮
  showCancelButton: boolean
  // 是否显示确认按钮
  showConfirmButton: boolean
  // 取消按钮文本
  cancelButtonText: string
  // 确认按钮文本
  confirmButtonText: string
  // 取消按钮类型
  cancelButtonType: string
  // 确认按钮类型
  confirmButtonType: string
  // 消息类型
  type?: 'success' | 'error' | 'warning' | 'info'
}

const props = withDefaults(defineProps<Props>(), {
  visible: false,
  title: '',
  message: '',
  showClose: true,
  showFooter: true,
  showCancelButton: true,
  showConfirmButton: true,
  cancelButtonText: '取消',
  confirmButtonText: '确定',
  cancelButtonType: 'info',
  confirmButtonType: 'default',
  type: 'info'
})

const emit = defineEmits(['update:visible', 'confirm', 'cancel', 'close'])

// 监听visible变化，当visible变为false时触发close事件
watch(
  () => props.visible,
  (newVal) => {
    if (!newVal) {
      emit('close')
    }
  }
)

// 处理取消事件
const handleCancel = () => {
  emit('update:visible', false)
  emit('cancel')
}

const localTitle = computed(() => {
  if (props.title) return props.title
  if (props.type === 'success') return '操作成功'
  if (props.type === 'error') return '错误信息'
  if (props.type === 'warning') return '告警信息'
  if (props.type === 'info') return ''
})

// 处理确认事件
const handleConfirm = () => {
  emit('confirm')
}
</script>

<style lang="scss" scoped>
.dialog-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3000;
}

.dialog-content {
  background-color: #fff;
  border-radius: 8px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  // border-bottom: 1px solid #e8e8e8;
}

.dialog-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
}

.dialog-close {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  &:hover {
    background-color: var(--bg-gray);
    color: var(--text-primary);
  }
}

.dialog-body {
  padding: 0 20px;
  color: var(--text-primary);
  font-weight: 500;
  text-align: center;
  min-height: 80px;
  font-size: 18px;
  // vertical-align: middle;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  padding: 16px 20px;
  // border-top: 1px solid #e8e8e8;
  gap: 12px;
}

// 过渡动画
.dialog-enter-active,
.dialog-leave-active {
  transition: opacity 0.3s ease;
}

.dialog-enter-from,
.dialog-leave-to {
  opacity: 0;
}

.dialog-enter-active .dialog-content,
.dialog-leave-active .dialog-content {
  transition: transform 0.3s ease;
}

.dialog-enter-from .dialog-content {
  transform: translate3d(0, -20px, 0);
}

.dialog-leave-to .dialog-content {
  transform: translate3d(0, 20px, 0);
}
</style>
